import React, { useState } from 'react';
import VerdictArea from '../VerdictArea';
import AntecedentsArea from '../AntecedentsArea';
import PlacApiResults from '../PlacApiResults';
import Modal from '../Modal';
import { INSPECTION_STATUS_VERDICT, INSPECTION_AREAS, ROLES } from '../../utils/constants';
import { formatDate } from '../../utils/helper';
import { getOfficialRecords } from '../../utils/propertyCardCrossCheck';
import config from '../../config/environment';
import './InspectionAreas.css';

const { APPROVED, REJECTED, IN_PROGRESS, PENDING } = INSPECTION_STATUS_VERDICT;
const { ANTECEDENTS, VERDICTS } = INSPECTION_AREAS;

const InspectionAreas = ({
    inspection,
    currentUser,
    onVerdictChange,
    onVerdictCommentsChange,
    onAntecedentsChange,
    onAntecedentsCommentsChange,
    onStartArea,
    onCancelAntecedents,
    onCancelVerdict,
    onApproveInspection,
    onRejectInspection,
    onRevertResult,
    enableApproveRejectButtons,
    enableStartButton,
    enableRevertResultButton,
    isVerdictAreaComplete,
    rejectedPhotosCount = 0,
    pendingPhotosCount = 0,
    onRetryPlacApi,
}) => {
    const [isRevertResultModalOpen, setIsRevertResultModalOpen] = useState(false);
    const [revertResultArea, setRevertResultArea] = useState(null);
    const showTechnicalError = currentUser?.role === ROLES.ADMINISTRATOR;
    const showPlacApiFields = [ROLES.INSPECTOR, ROLES.ADMINISTRATOR].includes(currentUser?.role);

    // Obtener estado visual para antecedentes
    const getAntecedentsState = () => {
        if (inspection?.antecedents?.status === APPROVED) return APPROVED;
        if (inspection?.antecedents?.status === REJECTED) return REJECTED;
        if (inspection?.antecedents?.status === IN_PROGRESS) return IN_PROGRESS;
        return PENDING;
    };

    // Obtener estado visual para veredicto
    const getVerdictState = () => {
        if (inspection?.verdict?.status === APPROVED) return APPROVED;
        if (inspection?.verdict?.status === REJECTED) return REJECTED;
        if (inspection?.verdict?.status === IN_PROGRESS) return IN_PROGRESS;
        return PENDING;
    };

    const handleRevertResultModal = (area) => {
        setRevertResultArea(area);
        setIsRevertResultModalOpen(true);
    };

    const handleRevertResult = () => {
        if (revertResultArea && onRevertResult) {
            onRevertResult(revertResultArea);
        }
        setIsRevertResultModalOpen(false);
    };

    return (
        <>
            {/* Inspection Area Section */}
            <div className="inspection-areas-section-card">
                <div className={`inspection-areas-section-card ${[APPROVED, REJECTED].includes(inspection.status) ? 'disabled' : ''}`}>
                    <h2 className="inspection-areas-section-title">
                        Área de Dictamen
                        {inspection.verdict?.status === IN_PROGRESS && (
                            <span className="assignment-label">
                                Asignado a: {inspection.verdict.assignedTo.name}
                            </span>
                        )}
                        <span className={`status-badge ${getVerdictState()}`}>
                            {getVerdictState() === APPROVED ? 'Aprobado' :
                                getVerdictState() === REJECTED ? 'Rechazado' :
                                    getVerdictState() === IN_PROGRESS ? 'En Progreso' : 'Pendiente'}
                        </span>
                    </h2>
                    
                    {inspection.verdict?.status === APPROVED || inspection.verdict?.status === REJECTED ? (
                        <div className="completion-details">
                            <div className="completion-header">
                                <h3>Información del Dictamen Completado</h3>
                                <div className="completion-meta">
                                    <p><strong>Completado por:</strong> {inspection.verdict.finishedBy}</p>
                                    <p><strong>Fecha:</strong> {formatDate(inspection.verdict.finishedAt)}</p>
                                </div>
                            </div>

                            <div className="verdict-details">
                                <h4>Evaluación Realizada:</h4>
                                <div className="verdict-fields">
                                    <div className="verdict-field">
                                        <div className="field-label">Nº de Chasis:</div>
                                        <div className="field-value">
                                            {inspection.verdict.verdictFields?.chassis || 'No especificado'}
                                        </div>
                                    </div>
                                    <div className="verdict-field">
                                        <div className="field-label">Nº de Motor:</div>
                                        <div className="field-value">
                                            {inspection.verdict.verdictFields?.engine || 'No especificado'}
                                        </div>
                                    </div>
                                    <div className="verdict-field">
                                        <div className="field-label">Plaqueta Serial Número:</div>
                                        <div className="field-value">
                                            {inspection.verdict.verdictFields?.serialPlate || 'No especificado'}
                                        </div>
                                    </div>
                                    <div className="verdict-field">
                                        <div className="field-label">Consecutivo Producción:</div>
                                        <div className="field-value">
                                            {inspection.verdict.verdictFields?.productionConsecutive || 'No especificado'}
                                        </div>
                                    </div>
                                    <div className="verdict-field">
                                        <div className="field-label">Licencia de Tránsito:</div>
                                        <div className="field-value">
                                            {inspection.verdict.verdictFields?.transitLicense || 'No especificado'}
                                        </div>
                                    </div>
                                </div>

                                {inspection.verdict.comments && (
                                    <div className="verdict-comments">
                                        <h4>Observaciones:</h4>
                                        <p>{inspection.verdict.comments}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <VerdictArea
                            verdict={inspection.verdict?.verdictFields || {}}
                            onVerdictChange={onVerdictChange}
                            comments={inspection.verdict?.comments || ''}
                            onCommentsChange={onVerdictCommentsChange}
                            state={getVerdictState()}
                            onStart={() => onStartArea(VERDICTS)}
                            onCancel={onCancelVerdict}
                            onApprove={() => onApproveInspection('inspection')}
                            onReject={() => onRejectInspection('inspection')}
                            isComplete={isVerdictAreaComplete()}
                            enableApproveRejectButtons={enableApproveRejectButtons(VERDICTS)}
                            enableStartButton={enableStartButton(VERDICTS)}
                            rejectedPhotosCount={rejectedPhotosCount}
                            pendingPhotosCount={pendingPhotosCount}
                        />
                    )}
                </div>
                
                {enableRevertResultButton(VERDICTS) && (
                    <div className="revert-result-button-container">
                        <button 
                            className="revert-result-button btn-primary" 
                            onClick={() => handleRevertResultModal(VERDICTS)}
                        >
                            Revertir Resultado
                        </button>
                    </div>
                )}
            </div>

            {/* Antecedents Area Section */}
            <div className="inspection-areas-section-card">
                <div className={`inspection-areas-section-card ${[APPROVED, REJECTED].includes(inspection.status) ? 'disabled' : ''}`}>
                    <h2 className="inspection-areas-section-title">
                        Área de Antecedentes
                        {inspection.antecedents?.status === IN_PROGRESS && (
                            <span className="assignment-label">
                                Asignado a: {inspection.antecedents.assignedTo.name}
                            </span>
                        )}
                        <span className={`status-badge ${getAntecedentsState()}`}>
                            {getAntecedentsState() === APPROVED ? 'Aprobado' :
                                getAntecedentsState() === REJECTED ? 'Rechazado' :
                                    getAntecedentsState() === IN_PROGRESS ? 'En Progreso' : 'Pendiente'}
                        </span>
                    </h2>
                    
                    {inspection.antecedents?.status === APPROVED || inspection.antecedents?.status === REJECTED ? (
                        <div className="completion-details">
                            <div className="completion-header">
                                <h3>Información de Antecedentes Completada</h3>
                                <div className="completion-meta">
                                    <p><strong>Completado por:</strong> {inspection.antecedents.finishedBy}</p>
                                    <p><strong>Fecha:</strong> {formatDate(inspection.antecedents.finishedAt)}</p>
                                </div>
                            </div>

                            {showPlacApiFields && (config.ENABLE_OFFICIAL_RECORDS || getOfficialRecords(inspection.antecedents)?.data) && (
                            <PlacApiResults
                                placaApi={getOfficialRecords(inspection.antecedents)}
                                vehicleData={inspection.vehicleData}
                                showConsultedAt
                                showTechnicalError={showTechnicalError}
                            />
                            )}

                            <div className="antecedents-details">
                                <h4>Evaluación Realizada:</h4>
                                <div className="antecedents-field">
                                    <span className="field-label">Antecedentes:</span>
                                    <span className="field-value">
                                        {inspection.antecedents.hasAntecedents === true ? 'SI' :
                                            inspection.antecedents.hasAntecedents === false ? 'NO' : 'No especificado'}
                                    </span>
                                </div>

                                {inspection.antecedents.antecedentsComments && (
                                    <div className="antecedents-comments">
                                        <h4>Observaciones:</h4>
                                        <p>{inspection.antecedents.antecedentsComments}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <AntecedentsArea
                            antecedents={inspection.antecedents?.hasAntecedents}
                            onAntecedentsChange={onAntecedentsChange}
                            antecedentsComments={inspection.antecedents?.antecedentsComments || ''}
                            onCommentsChange={onAntecedentsCommentsChange}
                            state={getAntecedentsState()}
                            onStart={(payload) => onStartArea(ANTECEDENTS, payload)}
                            onCancel={() => onCancelAntecedents(ANTECEDENTS)}
                            onApprove={() => onApproveInspection('antecedents')}
                            onReject={() => onRejectInspection('antecedents')}
                            isComplete={inspection.antecedents?.hasAntecedents !== undefined}
                            enableApproveRejectButtons={enableApproveRejectButtons(ANTECEDENTS)}
                            enableStartButton={enableStartButton(ANTECEDENTS)}
                            rejectedPhotosCount={rejectedPhotosCount}
                            pendingPhotosCount={pendingPhotosCount}
                            placaApi={getOfficialRecords(inspection.antecedents)}
                            vehicleData={inspection.vehicleData}
                            contactData={inspection.contactData}
                            onRetryPlacApi={onRetryPlacApi}
                            showTechnicalError={showTechnicalError}
                            showPlacApiFields={showPlacApiFields}
                        />
                    )}
                </div>
                
                {enableRevertResultButton(ANTECEDENTS) && (
                    <div className="revert-result-button-container">
                        <button 
                            className="revert-result-button btn-primary" 
                            onClick={() => handleRevertResultModal(ANTECEDENTS)}
                        >
                            Revertir Resultado
                        </button>
                    </div>
                )}
            </div>

            {/* Revert Result Modal */}
            <Modal
                title="Revertir Resultado"
                isOpen={isRevertResultModalOpen}
                onClose={() => setIsRevertResultModalOpen(false)}
                size="small"
                showCloseButton={false}
                closeOnBackdropClick={true}
            >
                <p>¿Estás seguro de querer revertir el resultado?</p>
                <p className="modal-warning">Importante: Esta acción no se puede deshacer.</p>
                <div className="modal-actions">
                    <button 
                        className="revert-result-button btn-primary cancel-button" 
                        onClick={() => setIsRevertResultModalOpen(false)}
                    >
                        Cancelar
                    </button>
                    <button 
                        className="revert-result-button btn-primary" 
                        onClick={handleRevertResult}
                    >
                        Confirmar
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default InspectionAreas;